/**
 * MCP Write Tool Handlers
 *
 * Implementations for tools that modify the IDF model.
 */

import type { IDFObject, IDFField } from '../../parser';
import type { MCPToolResult } from '../types';
import type { HandlerContext } from './readHandlers';

/**
 * Extended context for write operations
 */
export interface WriteHandlerContext extends HandlerContext {
  updateField: (objectId: string, fieldIndex: number, field: Partial<IDFField>) => void;
  updateObject: (objectId: string, updates: Partial<IDFObject>) => void;
  addObject: (obj: Omit<IDFObject, 'id'>) => string;
  removeObject: (objectId: string) => void;
  duplicateObject: (objectId: string) => string | null;
}

/**
 * Handle set_field_value tool
 */
export function handleSetFieldValue(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;
  const fieldIndex = args["fieldIndex"] as number;
  const value = args["value"] as string;

  const obj = ctx.getObjectById(objectId);
  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  if (fieldIndex < 0 || fieldIndex >= obj.fields.length) {
    // Extend fields array if needed
    const currentLength = obj.fields.length;
    if (fieldIndex >= currentLength) {
      const newFields = [...obj.fields];
      for (let i = currentLength; i <= fieldIndex; i++) {
        newFields.push({ value: '' });
      }
      newFields[fieldIndex] = { value };
      ctx.updateObject(objectId, { fields: newFields });
    }
  } else {
    ctx.updateField(objectId, fieldIndex, { value });
  }

  // Get updated object
  const updatedObj = ctx.getObjectById(objectId);

  return {
    success: true,
    content: {
      objectId,
      fieldIndex,
      oldValue: obj.fields[fieldIndex]?.value || '',
      newValue: value,
      object: updatedObj
        ? {
            id: updatedObj.id,
            className: updatedObj.className,
            name: updatedObj.fields[0]?.value || null,
          }
        : null,
    },
  };
}

/**
 * Handle set_multiple_fields tool
 */
export function handleSetMultipleFields(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;
  const fields = args["fields"] as Array<{ fieldIndex: number; value: string }>;

  const obj = ctx.getObjectById(objectId);
  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const changes: Array<{ fieldIndex: number; oldValue: string; newValue: string }> = [];

  // Build new fields array
  const maxIndex = Math.max(...fields.map((f) => f.fieldIndex), obj.fields.length - 1);
  const newFields = [...obj.fields];

  // Extend if needed
  for (let i = newFields.length; i <= maxIndex; i++) {
    newFields.push({ value: '' });
  }

  // Apply changes
  for (const update of fields) {
    const oldValue = newFields[update.fieldIndex]?.value || '';
    const existingField = newFields[update.fieldIndex];
    newFields[update.fieldIndex] = {
      ...(existingField || {}),
      value: update.value,
    };
    changes.push({
      fieldIndex: update.fieldIndex,
      oldValue,
      newValue: update.value,
    });
  }

  ctx.updateObject(objectId, { fields: newFields });

  return {
    success: true,
    content: {
      objectId,
      changesApplied: changes.length,
      changes,
    },
  };
}

/**
 * Handle add_object tool
 */
export function handleAddObject(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const className = args["className"] as string;
  const fieldValues = args["fields"] as string[];

  // Validate class name against schema
  if (ctx.schema && !ctx.schema.objectTypes.has(className.toLowerCase())) {
    return {
      success: false,
      error: `Unknown object class: ${className}. Use list_schema_object_types to see available classes.`,
    };
  }

  const fields: IDFField[] = fieldValues.map((value) => ({ value }));

  const newObj: Omit<IDFObject, 'id'> = {
    className,
    fields,
    leadingComments: [],
  };

  const newId = ctx.addObject(newObj);

  return {
    success: true,
    content: {
      objectId: newId,
      className,
      name: fields[0]?.value || null,
      fieldCount: fields.length,
    },
  };
}

/**
 * Handle remove_object tool
 */
export function handleRemoveObject(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;

  const obj = ctx.getObjectById(objectId);
  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const removedInfo = {
    className: obj.className,
    name: obj.fields[0]?.value || null,
  };

  ctx.removeObject(objectId);

  return {
    success: true,
    content: {
      removed: true,
      objectId,
      ...removedInfo,
    },
  };
}

/**
 * Handle duplicate_object tool
 */
export function handleDuplicateObject(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;
  const customName = args["newName"] as string | undefined;

  const original = ctx.getObjectById(objectId);
  if (!original) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const newId = ctx.duplicateObject(objectId);
  if (!newId) {
    return { success: false, error: 'Failed to duplicate object' };
  }

  // If custom name provided, update it
  if (customName) {
    ctx.updateField(newId, 0, { value: customName });
  }

  const newObj = ctx.getObjectById(newId);

  return {
    success: true,
    content: {
      originalObjectId: objectId,
      newObjectId: newId,
      className: original.className,
      originalName: original.fields[0]?.value || null,
      newName: newObj?.fields[0]?.value || null,
    },
  };
}

/**
 * Handle rename_object tool
 */
export function handleRenameObject(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const objectId = args["objectId"] as string;
  const newName = args["newName"] as string;
  const updateReferences = args["updateReferences"] !== false;

  const obj = ctx.getObjectById(objectId);
  if (!obj) {
    return { success: false, error: `Object not found: ${objectId}` };
  }

  const oldName = obj.fields[0]?.value || '';

  // Update the object name
  ctx.updateField(objectId, 0, { value: newName });

  // Update references in other objects
  let referencesUpdated = 0;
  if (updateReferences && oldName) {
    for (const otherObj of ctx.document.objects) {
      if (otherObj.id === objectId) continue;

      for (let i = 0; i < otherObj.fields.length; i++) {
        if (otherObj.fields[i]?.value === oldName) {
          ctx.updateField(otherObj.id, i, { value: newName });
          referencesUpdated++;
        }
      }
    }
  }

  return {
    success: true,
    content: {
      objectId,
      className: obj.className,
      oldName,
      newName,
      referencesUpdated,
    },
  };
}

/**
 * Handle batch_update tool
 */
export function handleBatchUpdate(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const updates = args["updates"] as Array<{
    objectId: string;
    fieldIndex: number;
    value: string;
  }>;

  const results: Array<{
    objectId: string;
    fieldIndex: number;
    success: boolean;
    error?: string;
  }> = [];

  for (const update of updates) {
    const obj = ctx.getObjectById(update.objectId);
    if (!obj) {
      results.push({
        objectId: update.objectId,
        fieldIndex: update.fieldIndex,
        success: false,
        error: 'Object not found',
      });
      continue;
    }

    try {
      ctx.updateField(update.objectId, update.fieldIndex, { value: update.value });
      results.push({
        objectId: update.objectId,
        fieldIndex: update.fieldIndex,
        success: true,
      });
    } catch (error) {
      results.push({
        objectId: update.objectId,
        fieldIndex: update.fieldIndex,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  const successCount = results.filter((r) => r.success).length;

  return {
    success: successCount > 0,
    content: {
      totalUpdates: updates.length,
      successCount,
      failureCount: updates.length - successCount,
      results,
    },
  };
}

/**
 * Handle apply_auto_fix tool
 */
export function handleApplyAutoFix(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const fixId = args["fixId"] as string;

  // Auto-fix implementations
  switch (fixId) {
    case 'missing-version': {
      // Add Version object
      const schemaVersion = ctx.schema?.version || '24.1';
      const majorVersion = schemaVersion.split('.').slice(0, 2).join('.');
      const newId = ctx.addObject({
        className: 'Version',
        fields: [{ value: majorVersion }],
        leadingComments: [],
      });
      return {
        success: true,
        content: {
          fixId,
          action: 'Added Version object',
          objectId: newId,
          version: majorVersion,
        },
      };
    }

    case 'missing-simulation-control': {
      // Add SimulationControl with default values
      const newId = ctx.addObject({
        className: 'SimulationControl',
        fields: [
          { value: 'No' }, // Do Zone Sizing Calculation
          { value: 'No' }, // Do System Sizing Calculation
          { value: 'No' }, // Do Plant Sizing Calculation
          { value: 'No' }, // Run Simulation for Sizing Periods
          { value: 'Yes' }, // Run Simulation for Weather File Run Periods
        ],
        leadingComments: [],
      });
      return {
        success: true,
        content: {
          fixId,
          action: 'Added SimulationControl object with default values',
          objectId: newId,
        },
      };
    }

    case 'missing-building': {
      // Add Building object
      const newId = ctx.addObject({
        className: 'Building',
        fields: [
          { value: 'Building 1' }, // Name
          { value: '0' }, // North Axis
          { value: 'Suburbs' }, // Terrain
          { value: '0.04' }, // Loads Convergence Tolerance Value
          { value: '0.4' }, // Temperature Convergence Tolerance Value
          { value: 'FullInteriorAndExterior' }, // Solar Distribution
          { value: '25' }, // Maximum Number of Warmup Days
          { value: '6' }, // Minimum Number of Warmup Days
        ],
        leadingComments: [],
      });
      return {
        success: true,
        content: {
          fixId,
          action: 'Added Building object with default values',
          objectId: newId,
        },
      };
    }

    default:
      return {
        success: false,
        error: `Unknown auto-fix ID: ${fixId}. Use get_available_auto_fixes to see available fixes.`,
      };
  }
}

/**
 * Handle create_from_template tool
 */
export function handleCreateFromTemplate(
  args: Record<string, unknown>,
  ctx: WriteHandlerContext
): MCPToolResult {
  if (!ctx.document) {
    return { success: false, error: 'No model loaded' };
  }

  const templateName = args["templateName"] as string;
  const customizations = args["customizations"] as Record<string, string> | undefined;

  // Template definitions
  const templates: Record<string, { className: string; fields: string[] }> = {
    simple_wall_construction: {
      className: 'Construction',
      fields: ['Wall Construction', 'Concrete Block', 'Insulation', 'Gypsum Board'],
    },
    simple_roof_construction: {
      className: 'Construction',
      fields: ['Roof Construction', 'Metal Roofing', 'Roof Insulation', 'Ceiling Tile'],
    },
    office_occupancy_schedule: {
      className: 'Schedule:Compact',
      fields: [
        'Office Occupancy',
        'Fraction',
        'Through: 12/31',
        'For: Weekdays',
        'Until: 08:00,0.0',
        'Until: 18:00,1.0',
        'Until: 24:00,0.0',
        'For: Weekends Holiday',
        'Until: 24:00,0.0',
      ],
    },
    always_on_schedule: {
      className: 'Schedule:Constant',
      fields: ['Always On', 'On/Off', '1'],
    },
    always_off_schedule: {
      className: 'Schedule:Constant',
      fields: ['Always Off', 'On/Off', '0'],
    },
  };

  const template = templates[templateName];
  if (!template) {
    return {
      success: false,
      error: `Unknown template: ${templateName}. Available templates: ${Object.keys(templates).join(', ')}`,
    };
  }

  // Apply customizations
  const fields = [...template.fields];
  if (customizations) {
    for (const [key, value] of Object.entries(customizations)) {
      const index = parseInt(key, 10);
      if (!isNaN(index) && index < fields.length) {
        fields[index] = value;
      }
    }
  }

  const newId = ctx.addObject({
    className: template.className,
    fields: fields.map((v) => ({ value: v })),
    leadingComments: [],
  });

  return {
    success: true,
    content: {
      templateName,
      objectId: newId,
      className: template.className,
      name: fields[0],
    },
  };
}

/**
 * All write handlers
 */
export const writeHandlers: Record<
  string,
  (args: Record<string, unknown>, ctx: WriteHandlerContext) => MCPToolResult
> = {
  set_field_value: handleSetFieldValue,
  set_multiple_fields: handleSetMultipleFields,
  add_object: handleAddObject,
  remove_object: handleRemoveObject,
  duplicate_object: handleDuplicateObject,
  rename_object: handleRenameObject,
  batch_update: handleBatchUpdate,
  apply_auto_fix: handleApplyAutoFix,
  create_from_template: handleCreateFromTemplate,
};
